import React, {
    FormEvent,
    ReactElement, useEffect, useState,
} from "react";
import { Form } from "react-bootstrap";
import { AiOutlineEdit, AiOutlinePlus } from "react-icons/ai";
import { BsTrash } from "react-icons/bs";
import { Loading } from "../../../../components";
import Avatar from "../../../../components/avatar/Avatar";
import Confirm from "../../../../components/confirm/Confirm";
import { Filters } from "../../../../components/filters/Filters";
import Agents from "../../../../components/pickers/agents/Agents";
import ChannelTags from "../../../../components/pickers/channel-tags/ChannelTags";
import EmailAliases from "../../../../components/pickers/email-aliases/EmailAliases";
import Integrations from "../../../../components/pickers/integrations/Integrations";
import Select, { Option } from "../../../../components/select/Select";
import useRouting, { Link, useParams } from "../../../../contexts/Routing";
import {
    AssignmentType, GetQueueDocument, useDeleteQueueMutation,
    useGetQueueLazyQuery, useGetQueuesQuery, useSaveQueueMutation,
    useSaveDefaultQueueMutation,
    TransferSettingsDocument,
    QueueMemberInput,
    QueueMember,
    QueueMemberRole,
    User,
} from "../../../../__generated__/api";
import { ErrorRemote } from "../../../error/Error";

const ROUTING_OPTIONS: Option[] = [
    {
        label: "None",
        value: AssignmentType.None,
    },
    {
        label: "Round Robin",
        value: AssignmentType.RoundRobin,
    },
    {
        label: "Weight based (Coming Soon)",
        value: "weight",
    },
];

const ROLE_TO_NAME = {
    [QueueMemberRole.Assignee]: "Assignee",
    [QueueMemberRole.Observer]: "Observer",
};

const ROLE_OPTIONS = [
    {
        label: ROLE_TO_NAME[QueueMemberRole.Assignee],
        value: QueueMemberRole.Assignee,
    },
    {
        label: ROLE_TO_NAME[QueueMemberRole.Observer],
        value: QueueMemberRole.Observer,
    },
];

function UserManager({
    members,
    onChange,
    canSelectRole,
}: {
    members: QueueMember[];
    onChange: (members: QueueMember[]) => void;
    canSelectRole: boolean
}) {
    const [currentUsers, setCurrentUsers] = useState<User[]>();

    const [addingRole, setAddingRole] = useState<QueueMemberRole>(QueueMemberRole.Assignee);

    const setUsersRole = async (users: User[], role: QueueMemberRole) => {
        const updateSet = new Set(users.toIdList("id"));
        let newSet = members.map(member => (updateSet.has(member.userId) ? {
            ...member,
            role,
        } : member));

        newSet = newSet.concat(users
            .exclude(members.toIdList("user"), user => user.id)
            .map(user => ({
                role,
                userId: user.id,
                user,
            })));

        onChange(newSet);
    };

    const deleteUser = async (userId: string) => {
        onChange(members.filter(member => member.userId !== userId));
    };

    return (
        <div className="">

            <div className="list-group">
                {members.isEmpty && (
                    <div className="text-center text-light border fst-italic p-2 rounded">
                        There are no members of this queue.
                    </div>
                )}
                {members?.sortBy(user => user.user.name?.toLowerCase()).map(member => (

                    <div className="d-flex w-100 justify-content-between list-group-item align-items-center" key={member.userId}>
                        <div className="d-flex flex-grow-1 align-items-center">
                            <div className="col-3">
                                <Avatar
                                    src={member.user.avatarUrl as string}
                                    isCircle
                                    variant="xs"
                                />
                                {member.user.name}
                            </div>
                            {canSelectRole && (
                                <Select
                                    className="mx-3 col-3"
                                    minRequired={1}
                                    options={ROLE_OPTIONS}
                                    value={{
                                        label: ROLE_TO_NAME[member.role],
                                        value: member.role,
                                    }}
                                    onChange={role => setUsersRole([member.user], role.value as QueueMemberRole)}
                                />
                            )}

                        </div>

                        <div className="ms-2 text-light">
                            <button
                                className="btn btn-none text-gray-500 p-0"
                                type="button"
                                onClick={() => deleteUser(member.userId)}
                            >
                                <BsTrash />
                            </button>
                        </div>

                    </div>

                ))}

            </div>
            <div>
                <small className="text-light mb-1 d-block mt-3">
                    To assign a new member, select the agent below
                    {canSelectRole ? ", select the role, " : " "}
                    and click &quot;Add&quot;.
                </small>
                <div
                    className="d-flex justify-content-between align-items-center"
                >
                    <div className="d-flex col align-items-center">
                        <span className="mx-2 text-light">
                            Agents:
                        </span>
                        <Agents
                            onSelected={(userId, user) => setCurrentUsers(user)}
                            value={currentUsers?.toIdList("id")}
                            className="w-100"
                            isMulti
                            exclude={members.toIdList("userId")}
                        />
                    </div>

                    {canSelectRole && (
                        <div className="d-flex col-3 align-items-center">
                            <span className="mx-2 text-light">
                                Role:
                            </span>
                            <Select
                                className="w-100"
                                minRequired={1}
                                options={ROLE_OPTIONS}
                                onChange={role => setAddingRole(role.value as QueueMemberRole)}
                                value={!addingRole ? undefined : {
                                    value: addingRole,
                                    label: ROLE_TO_NAME[addingRole],
                                }}
                            />
                        </div>
                    )}

                    <button
                        type="button"
                        className="btn btn-primary py-2 col-1 ms-2"
                        onClick={evt => {
                            if (!currentUsers?.isNotEmpty || !addingRole) {
                                return;
                            }
                            setUsersRole(currentUsers, addingRole);
                            setAddingRole(QueueMemberRole.Assignee);
                            setCurrentUsers([]);
                        }}
                    >
                        Add
                    </button>
                </div>
            </div>

        </div>
    );
}

function DisplayQueue({ id }: {id: string}): ReactElement {
    const [loadQuery, { data, error, loading: isLoading }] = useGetQueueLazyQuery({
        fetchPolicy: "no-cache",
        variables: {
            id,
        },
    });

    const [deleteQueue] = useDeleteQueueMutation();

    const { Paths, historyPush } = useRouting();

    const [save] = useSaveQueueMutation();

    const [name, setName] = useState(() => data?.queue?.name ?? "");
    const [userAssignmentType, setUserAssignementType] = useState(() => data?.queue?.userAssignmentType ?? AssignmentType.None);
    const [description, setDescription] = useState(() => data?.queue?.description ?? "");
    const [members, setMembers] = useState(() => data?.queue?.members ?? []);
    const [channelTags, setChannelTags] = useState<string[]>(() => data?.queue?.channelTags ?? []);
    const [emailAliasIds, setEmailAliasIds] = useState<string[]>(() => data?.queue?.emailAliasIds ?? []);
    const [integrationIds, setIntegrationIds] = useState<string[]>(() => data?.queue?.integrationIds ?? []);
    const [displayDelete, setDisplayDelete] = useState(false);

    useEffect(() => {
        if (!data?.queue) {
            return;
        }
        setName(data.queue.name ?? "");
        setUserAssignementType(data.queue.userAssignmentType ?? AssignmentType.None);
        setDescription(data.queue.description ?? "");
        setMembers(data.queue.members ?? []);
        setChannelTags(data.queue.channelTags ?? []);
        setEmailAliasIds(data.queue.emailAliasIds ?? []);
        setIntegrationIds(data.queue.integrationIds ?? []);
    }, [data?.queue]);

    if (isLoading) {
        return <Loading />;
    }
    if (error) {
        return <ErrorRemote error={error} />;
    }

    if (id !== "new" && !data?.queue) {
        loadQuery({
            variables: {
                id,
            },
        });
    }

    const onSubmit = (event: FormEvent) => {
        event.preventDefault();
        (async () => {
            await save({
                refetchQueries: [
                    { query: GetQueueDocument },
                    { query: TransferSettingsDocument },
                ],
                variables: {
                    id: id !== "new" ? id : undefined,
                    queue: {
                        name,
                        description,
                        members: members.map(member => ({
                            role: member.role,
                            userId: member.userId,
                        })),
                        channelTags,
                        emailAliasIds,
                        integrationIds,
                        userAssignmentType,
                    },
                },
            });

            historyPush(Paths.ADMINISTRATOR_SETTING, {
                setting: "queues",
            });
        })();
    };

    const onDelete = async () => {
        await deleteQueue({
            refetchQueries: [
                { query: GetQueueDocument },
            ],
            variables: {
                id,
            },
        });
        historyPush(Paths.ADMINISTRATOR_SETTING, {
            setting: "queues",
        });
    };

    return (
        <>
            <Confirm confirmText="Delete" onConfirm={onDelete} shouldShow={displayDelete} onHide={() => setDisplayDelete(false)}>
                <div>
                    Are you sure you want to delete the queue:
                    {" "}
                    <div className="mt-2 p-2 rounded alert alert-danger">
                        {name}
                    </div>

                </div>
            </Confirm>
            <form className="flex-grow-1" onSubmit={onSubmit}>

                <div className="border p-3 rounded mb-4 bg-white">
                    <h5 className="mb-3 text-light">
                        Queue
                    </h5>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Name</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="My Queue"
                            required
                            onChange={evt => setName(evt.target.value)}
                            value={name as string}
                        />
                        <Form.Text>
                            The name of the queue.
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">
                            Description
                            {" "}
                            <small className="text-light">(Optional)</small>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="This queue is used for .."
                            as="textarea"
                            onChange={evt => setDescription(evt.target.value)}
                            value={description as string}
                        />
                        <Form.Text>
                            Any information you want to leave to help you understand what this queue is created for.
                        </Form.Text>
                    </Form.Group>
                </div>

                <div className="border p-3 rounded bg-white mb-4">

                    <h5 className="mb-3 text-light">
                        Members
                    </h5>

                    {userAssignmentType !== AssignmentType.None ? (
                        <p className="text-light">
                            These are the members of the queue. Members can either be assignees or obeservers.
                            Because routing is enabled, tickets will be assignable to &quot;Assignee&quot; users.
                            Observers have the ability to see tickets but they are not automatically assigned to these users.
                        </p>
                    ) : (
                        <p className="text-light">
                            Below are the members of the queue that are able to see the tickets assigned to this queue.
                        </p>
                    )}

                    <UserManager
                        members={members as QueueMember[]}
                        onChange={setMembers}
                        canSelectRole={userAssignmentType !== AssignmentType.None}
                    />

                </div>

                <div className="border p-3 rounded bg-white">
                    <h5 className="mb-3 text-light">
                        Routing
                    </h5>

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Channel Tags</Form.Label>
                        <ChannelTags
                            placeholder="Please pick a list of channel tags"
                            isMulti
                            canCreate
                            onSelected={tags => setChannelTags(tags)}
                            value={channelTags}
                        />
                        <Form.Text>
                            Which tags should be associated with this queue. To manage channel tags, visit
                            {" "}
                            <Link
                                to={Paths.ADMINISTRATOR_SETTING}
                                params={{ setting: "channel-tags" }}
                                className="link-info text-decoration-none"
                            >
                                Channel Tag Settings
                            </Link>
                            .
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Integrations</Form.Label>
                        <Integrations
                            placeholder="Please pick the integrations"
                            isMulti
                            onSelected={integrations => setIntegrationIds(integrations)}
                            value={integrationIds}
                        />
                        <Form.Text>
                            If integrations create inbound tickets, force all created inbound tickets from these Integrations
                            to route to the currently configuring queue.
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Email Aliases</Form.Label>
                        <EmailAliases
                            placeholder="Please pick the email alias"
                            isMulti
                            onSelected={emails => setEmailAliasIds(emails)}
                            value={emailAliasIds}
                        />
                        <Form.Text>
                            Configure email aliases to automatically route to the current queue by configuring which aliases
                            should route to the queue in the above list. To configure more email aliases, view
                            {" "}
                            <Link
                                to={Paths.ADMINISTRATOR_SETTING}
                                params={{ setting: "channels-emails" }}
                                className="link-info text-decoration-none"
                            >
                                Email Channel settings
                            </Link>
                            .
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">
                            Routing
                        </Form.Label>
                        <Select
                            placeholder="None"
                            options={ROUTING_OPTIONS}
                            value={!userAssignmentType ? undefined
                                : ROUTING_OPTIONS.find(option => option.value === userAssignmentType)}
                            onChange={value => setUserAssignementType(value.value as AssignmentType)}
                        />
                        <Form.Text>
                            Do you want to automatically route requests to individual users?
                        </Form.Text>
                    </Form.Group>
                    <Confirm
                        shouldShow={userAssignmentType?.toString() === "weight"}
                        onHide={() => setUserAssignementType(AssignmentType.None)}
                    >
                        <div>
                            Sorry this routing type is not yet supported. However we want to hear from you.
                            Please contact us and let us know how you would like to route your requests and we will try to help
                        </div>
                    </Confirm>
                </div>

                <div className="d-flex justify-content-end mt-3 fixed-bottom border-top p-3 bg-white">
                    {id !== "new" && (
                        <button type="button" className="btn btn-link text-danger me-4" onClick={() => setDisplayDelete(true)}>
                            Delete
                        </button>
                    )}
                    <button type="submit" className="btn btn-primary">
                        Save
                    </button>
                </div>
            </form>
        </>
    );
}

function DisplayList(): ReactElement {
    const { data, error, loading: isLoading } = useGetQueuesQuery({
        fetchPolicy: "no-cache",
    });
    const [saveDefaultQueue] = useSaveDefaultQueueMutation();
    const { Paths } = useRouting();
    const [defaultQueueId, setDefaultQueueId] = useState(data?.defaultQueue?.id);
    const [isDirty, setIsDirty] = useState(false);

    useEffect(() => {
        setDefaultQueueId(data?.defaultQueue?.id);
    }, [data?.defaultQueue?.id]);

    if (isLoading) {
        return <Loading />;
    }
    if (error) {
        return <ErrorRemote error={error} />;
    }

    const saveDefault = () => {
        setIsDirty(false);
        saveDefaultQueue({
            variables: {
                id: defaultQueueId,
            },
        });
    };

    const queueList = data?.queues.sortBy(queue => queue.name.toLowerCase());

    const defaultQueue = queueList?.find(queue => queue.id === defaultQueueId);
    return (
        <div className="d-flex align-items-stretch flex-column">
            {queueList?.isNotEmpty && (
                <div className="border p-3 rounded bg-white mb-3">

                    <Form.Group>
                        <Form.Label className="fw-bolder">Default Queue</Form.Label>
                        <Select
                            options={queueList.map(queue => ({
                                label: queue.name,
                                value: queue.id,
                            }))}
                            value={defaultQueue ? ({
                                label: defaultQueue.name,
                                value: defaultQueue.id as string,
                            }) : undefined}
                            onChange={current => {
                                setDefaultQueueId(current?.value);
                                setIsDirty(true);
                            }}
                            placeholder="What is your default queue?"
                            isClearable
                        />
                        <Form.Text>
                            Which queue should tickets be assigned to if not in any channel.
                        </Form.Text>
                    </Form.Group>
                    {isDirty && (
                        <div className="d-flex justify-content-end">
                            <button className="btn btn-secondary btn-sm" type="button" onClick={saveDefault}>
                                Save
                            </button>
                        </div>
                    )}

                </div>

            )}
            <div className="border p-3 rounded bg-white">
                {queueList?.isNotEmpty ? (
                    <>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="text-light mb-0">
                                Queues
                            </h5>

                            <Link
                                to={Paths.ADMINISTRATOR_SETTING_ID}
                                params={{
                                    id: "new",
                                    setting: "queues",
                                }}
                                className="btn btn-none btn-sm"
                            >
                                <AiOutlinePlus />
                                {" "}
                                Queue
                            </Link>

                        </div>
                        <div className="list-group">
                            {queueList.map(queue => (
                                <Link
                                    to={Paths.ADMINISTRATOR_SETTING_ID}
                                    params={{
                                        id: queue.id,
                                        setting: "queues",
                                    }}
                                    className="list-group-item list-group-item-action flex-column align-items-start"
                                >
                                    <div className="d-flex w-100 align-items-end mb-2">
                                        <h5 className="mb-0">
                                            {queue.name}
                                            {" "}
                                        </h5>
                                    </div>
                                    <small>{queue.description}</small>
                                    <div className="d-flex w-100 align-items-end mt-3">
                                        {queue.members.limit(5).map((member, index) => (
                                            <Avatar
                                                src={member.user.avatarUrl as string}
                                                isCircle
                                                variant="xs"
                                                className={index === 0 ? "" : "ms-n2"}
                                            />
                                        ))}
                                        <div className="ms-2 text-light">
                                            {queue.members.length}
                                            {" "}
                                            Users
                                        </div>

                                    </div>

                                </Link>

                            ))}

                        </div>
                        <div className="d-flex justify-content-end align-items-center mt-3">

                            <Link
                                to={Paths.ADMINISTRATOR_SETTING_ID}
                                params={{
                                    id: "new",
                                    setting: "queues",
                                }}
                                className="btn btn-secondary btn-sm"
                            >
                                <AiOutlinePlus />
                                {" "}
                                Queue
                            </Link>
                        </div>

                    </>
                ) : (
                    <div className="d-flex flex-column align-items-center justify-content-center h-100 text-light my-4">
                        <h5 className="text-light">
                            Queues
                        </h5>
                        <Link
                            to={Paths.ADMINISTRATOR_SETTING_ID}
                            params={{
                                id: "new",
                                setting: "queues",
                            }}
                            className="btn btn-secondary btn-sm my-3"
                        >
                            <AiOutlinePlus />
                            {" "}
                            Add Queue
                        </Link>
                        <small>
                            Queues allow you to apply routing and rules to incoming tickets.
                            Want all HR tickets to go to the same team?
                            Want all external requests from Acme corp to go to that CSM, use Queues to set that up.
                        </small>
                    </div>

                )}

            </div>

        </div>
    );
}

export default function QueueSettings(): ReactElement {
    const { Paths } = useRouting();
    const { id } = useParams(Paths.ADMINISTRATOR_SETTING_ID);
    return id ? <DisplayQueue id={id} /> : <DisplayList />;
}
